import { useTheme } from '../../styles/ThemeContext';
import { fmtEur } from '../../utils/helpers';

/**
 * Step 5: Final Confirmation and Save
 */
export function Step5Confirmation({ sd, units, products, orderNumber, totalQty, totalValue, confirmSaved, confirmLoading, confirmError, onConfirmSave, onSaveDraft, draftSaveLoading, onPrint, onNewPackingList, onBack, onViewMyOrders }) {
  const { theme } = useTheme();
  const c = theme.colors;
  const hasDangerousGoods = products.some((p) => p.dg === 'Yes');
  const dgCount = products.filter((p) => p.dg === 'Yes').length;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
        <span style={theme.sectTag}>STEP 05</span>
        <h2 style={{ fontSize: 20, fontWeight: 700, margin: 0 }}>Confirmation</h2>
      </div>

      {/* Summary stats */}
      <div style={theme.card}>
        <div style={{ fontSize: 9, color: c.accent, letterSpacing: 1, marginBottom: 12, fontFamily: 'monospace' }}>OVERVIEW ──────────────────</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr', gap: 14 }}>
          {[['Reference', orderNumber], ['Shipping Units', units.length], ['Products', products.length], ['Total Qty', totalQty], ['Total Value', `€${fmtEur(totalValue)}`]].map(([l, v]) => (
            <div key={l} style={{ background: c.bgInput, padding: 14, borderRadius: 4, textAlign: 'center' }}>
              <div style={{ fontSize: 9, color: c.textSecondary, fontFamily: 'monospace', letterSpacing: 1, marginBottom: 4 }}>{l}</div>
              <div style={{ fontSize: l === 'Reference' ? 13 : 20, fontWeight: 700, color: c.accent, fontFamily: 'monospace' }}>{v}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Addresses summary */}
      <div style={theme.card}>
        <div style={{ fontSize: 9, color: c.accent, letterSpacing: 1, marginBottom: 12, fontFamily: 'monospace' }}>ADDRESSES ──────────────────</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
          {[
            ['Collection', sd.collCompany, sd.collAddress, `${sd.collPostal} ${sd.collCity}`.trim(), sd.collCountry, sd.collContact, sd.collPhone, sd.collEmail],
            ['Delivery', sd.delCompany, sd.delAddress, `${sd.delPostal} ${sd.delCity}`.trim(), sd.delCountry, sd.delContact, sd.delPhone, sd.delEmail]
          ].map(([title, ...lines]) => (
            <div key={title}>
              <div style={{ fontSize: 10, fontWeight: 700, color: c.accent, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>{title}</div>
              <div style={{ fontSize: 12, lineHeight: 1.8, color: c.text }}>{lines.filter(Boolean).map((l, i) => <div key={i}>{l}</div>)}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Shipping units summary */}
      <div style={theme.card}>
        <div style={{ fontSize: 9, color: c.accent, letterSpacing: 1, marginBottom: 12, fontFamily: 'monospace' }}>SHIPPING UNITS ──────────────────</div>
        <div style={{ border: '1px solid ' + c.border, borderRadius: 4, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: c.bgInput }}>
                {['#', 'Type', 'Nr', 'L x W x H', 'Tare', 'Total', 'Items'].map((h) => (
                  <th key={h} style={{ textAlign: 'left', padding: '7px 10px', fontSize: 9, fontFamily: 'monospace', color: c.textSecondary, letterSpacing: 1, borderBottom: '1px solid ' + c.border }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {units.map((u, i) => (
                <tr key={u.id} style={{ borderBottom: '1px solid ' + c.border, background: i % 2 ? c.bg : 'transparent' }}>
                  <td style={{ padding: '7px 10px', fontFamily: 'monospace', color: c.accent, fontSize: 11 }}>{String(i + 1).padStart(2, '0')}</td>
                  <td style={{ padding: '7px 10px', fontWeight: 500 }}>{u.type}</td>
                  <td style={{ padding: '7px 10px' }}><span style={{ background: c.badge, padding: '2px 8px', fontSize: 10, fontFamily: 'monospace', borderRadius: 2 }}>{u.number}</span></td>
                  <td style={{ padding: '7px 10px', fontFamily: 'monospace', fontSize: 11 }}>{u.length} x {u.width} x {u.height} cm</td>
                  <td style={{ padding: '7px 10px', fontFamily: 'monospace', fontSize: 11 }}>{u.tare} kg</td>
                  <td style={{ padding: '7px 10px', fontFamily: 'monospace', fontSize: 11 }}>{u.totalWeight ? u.totalWeight + ' kg' : '—'}</td>
                  <td style={{ padding: '7px 10px', fontSize: 11, color: c.textSecondary }}>{products.filter((p) => p.unitId === u.id).length} products</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* DG warning */}
      {hasDangerousGoods && (
        <div style={{ background: c.errorBg, border: '1px solid ' + c.errorBorder, borderRadius: 4, padding: 16, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 24 }}>⚠</span>
          <div>
            <div style={{ fontWeight: 600, color: c.error, marginBottom: 2 }}>Dangerous Goods</div>
            <div style={{ fontSize: 12, color: c.text }}>This packing list contains {dgCount} DG item(s). Please ensure the correct DG documentation is included.</div>
          </div>
        </div>
      )}

      {/* Confirmation */}
      {confirmSaved ? (
        <div style={{ background: c.successBg, border: '1px solid ' + c.successBorder, borderRadius: 4, padding: 24, textAlign: 'center' }}>
          <div style={{ fontSize: 32, marginBottom: 8 }}>✓</div>
          <div style={{ fontWeight: 700, fontSize: 18, marginBottom: 6, color: c.success }}>Order Saved!</div>
          <div style={{ fontSize: 13, color: c.text, marginBottom: 6 }}>Order number: <b style={{ color: c.accent, fontFamily: 'monospace', fontSize: 15 }}>{confirmSaved}</b></div>
          <div style={{ fontSize: 12, color: c.textSecondary, marginBottom: 20 }}>The packing list has been saved. You can view all your orders via "My Orders".</div>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
            <button onClick={onPrint} style={theme.btnG}>🖨 Print / PDF</button>
            <button onClick={onViewMyOrders} style={theme.btnG}>📋 View My Orders</button>
            <button onClick={onNewPackingList} style={theme.btnP}>+ New Packing List</button>
          </div>
        </div>
      ) : (
        <div style={{ background: c.accentBg, border: '1px solid ' + c.accentBorder, borderRadius: 4, padding: 24, textAlign: 'center' }}>
          <div style={{ fontSize: 32, marginBottom: 8 }}>📋</div>
          <div style={{ fontWeight: 700, fontSize: 18, marginBottom: 6 }}>Confirm Packing List</div>
          <div style={{ fontSize: 12, color: c.textSecondary, marginBottom: 16, maxWidth: 500, margin: '0 auto 16px' }}>Review the details above. Click "Confirm and Save" to save the order to the database.</div>
          {confirmError && <div style={{ color: c.error, fontSize: 12, marginBottom: 12 }}>{confirmError}</div>}
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
            <button onClick={onBack} style={theme.btnG}>← Back to Preview</button>
            {onSaveDraft && (
              <button onClick={onSaveDraft} disabled={draftSaveLoading} style={{ ...theme.btnG, padding: '12px 20px', fontSize: 14, borderColor: c.accent, color: c.accent, opacity: draftSaveLoading ? 0.7 : 1, cursor: draftSaveLoading ? 'not-allowed' : 'pointer' }}>{draftSaveLoading ? 'Saving...' : 'Save draft'}</button>
            )}
            <button onClick={onConfirmSave} disabled={confirmLoading} style={{ ...theme.btnP, padding: '12px 28px', fontSize: 14, opacity: confirmLoading ? 0.7 : 1, cursor: confirmLoading ? 'not-allowed' : 'pointer' }}>{confirmLoading ? 'Saving...' : '✓ Confirm and Save'}</button>
          </div>
        </div>
      )}
    </div>
  );
}
